import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { AgentOrbComponent } from '../../agent-orb/agent-orb.component';
import {
  createMiniSwarmWaitingLoopFrames,
  createSplitSwarmDistributeFrames,
  MINI_DISTRIBUTION_LOOP_DURATION,
  MINI_ORBI_SIZES,
  SPLIT_SWARM_DISTRIBUTE_DURATION,
  SWARM_SPARK_COUNT,
} from '../../agent-orb/orbi-swarm-keyframes';

export type OrbiDistributeMode = 'place' | 'sync';
export type OrbiDistributeOutcome = 'success' | 'partial' | 'failed';

export interface OrbiDistributeResult {
  outcome: OrbiDistributeOutcome;
  successCount?: number;
  failedCount?: number;
  /** Übersetzter Grund, warum Kurse scheiterten (nur Kurs-Sync). */
  reason?: string;
}

/** Lab-Sequenz-Id — auch der `data-sequence`-Wert an allen Orbs der Szene. */
const SEQUENCE_ID = 'split-swarm-distribute';

/** Farben von Ring und Funken, Tutor-Palette des Labs (`ORBI_TYPES.tutor`:
 *  bandA2 / bandB3 / fill3, vgl. core/mini-orbi-swarm.js). */
const SWARM_COLORS = { a: '#ff83f3', b: '#69bfff', c: '#9f8cff' };

/**
 * Verteil-Modal nach „Platzieren" und „Kurs-Sync starten" (Platzierungen-
 * Seite, `PlacementsSiteCardComponent`).
 *
 * Spielt die Lab-Sequenz `split-swarm-distribute` unverändert ab: Orbi
 * zerplatzt einmal in sieben Mini-Orbis, die sich verteilen; danach
 * wiederholen NUR die Mini-Orbis ihre Wartezyklen (je 4,2 s), bis der
 * Aufrufer `complete()` meldet. Der laufende Zyklus landet noch sauber,
 * dann bleibt der verteilte Schwarm stehen und das Ergebnis-Symbol erscheint
 * — und bleibt, bis der Nutzer schließt (auch nach einer Stunde).
 *
 * Kommt `complete()` vor dem Ende des Zerplatzens (wenige Kurse), läuft der
 * Split trotzdem ganz durch; es startet nur kein Wartezyklus. So hält es auch
 * der Lab-Vertrag (sequences/split-swarm-distribute/sequence.js).
 *
 * Kurs-Sync: sobald der Server den Job angenommen hat, ruft der
 * `CourseSyncJobTrackerService` `allowBackground()` — dann erklärt ein
 * Hinweis, dass der Server allein weiterarbeitet, und „Weiterarbeiten"
 * schließt das Modal. Das Ergebnis kommt später als eigenes Modal mit
 * `initialResult`: der Schwarm steht sofort verteilt da, ohne erneutes
 * Zerplatzen, und das Symbol erscheint.
 */
@Component({
  selector: 'app-orbi-distribute-dialog',
  templateUrl: './orbi-distribute-dialog.component.html',
  styleUrl: './orbi-distribute-dialog.component.scss',
})
export class OrbiDistributeDialogComponent implements AfterViewInit, OnDestroy {
  @Input() mode: OrbiDistributeMode = 'place';

  /** Öffnet direkt im Endzustand (Ergebnis eines im Hintergrund gelaufenen
   *  Kurs-Syncs, siehe `CourseSyncJobTrackerService`). */
  @Input() initialResult: OrbiDistributeResult | null = null;

  /** Der Vorgang läuft auf dem Server weiter — Hinweis + „Weiterarbeiten". */
  backgroundAvailable = false;

  readonly sequenceId = SEQUENCE_ID;
  readonly swarmColors = SWARM_COLORS;
  readonly miniSizes = MINI_ORBI_SIZES;
  readonly sparks = Array.from({ length: SWARM_SPARK_COUNT }, (_, index) => ({
    color: `var(--swarm-color-${['a', 'b', 'c'][index % 3]})`,
    length: `${8 + (index % 4) * 3}px`,
  }));

  /** Erst gesetzt, wenn die Animation gelandet ist — steuert Symbol, Text
   *  und den Schließen-Knopf. */
  result: OrbiDistributeResult | null = null;

  @ViewChild('mainOrb') private mainOrb?: AgentOrbComponent;
  @ViewChild('ring') private ringRef?: ElementRef<HTMLElement>;
  @ViewChildren('spark') private sparkRefs?: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('miniBody') private miniBodyRefs?: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('miniShadow') private miniShadowRefs?: QueryList<ElementRef<HTMLElement>>;

  private pendingResult: OrbiDistributeResult | null = null;
  private completionRequested = false;
  private resolveCompletion: () => void = () => undefined;
  private readonly completion = new Promise<void>((resolve) => (this.resolveCompletion = resolve));
  private destroyed = false;
  private readonly animations = new Set<Animation>();
  private readonly reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(
    protected dialogRef: NbDialogRef<OrbiDistributeDialogComponent>,
    private cdr: ChangeDetectorRef,
  ) {}

  get done(): boolean {
    return !!this.result;
  }

  get titleKey(): string {
    return `dialogs.orbiDistribute.${this.mode}.${this.result ? 'titleDone' : 'title'}`;
  }

  get messageKey(): string {
    if (!this.result) return `dialogs.orbiDistribute.${this.mode}.running`;
    // Mit bekanntem Grund kein „Bitte versuche es erneut" — der Grund sagt,
    // was vorher zu tun ist.
    const state = this.result.outcome === 'failed' && this.result.reason ? 'failedReason' : this.result.outcome;
    return `dialogs.orbiDistribute.${this.mode}.${state}`;
  }

  get messageParams(): Record<string, number> {
    return {
      success: this.result?.successCount ?? 0,
      failed: this.result?.failedCount ?? 0,
    };
  }

  get resultIcon(): string {
    switch (this.result?.outcome) {
      case 'partial': return 'alert-triangle-outline';
      case 'failed': return 'close-outline';
      default: return 'checkmark-outline';
    }
  }

  /** Abschlusssignal vom Aufrufer (Lab: `course-distribution-complete`).
   *  Nur das erste zählt. */
  complete(result: OrbiDistributeResult): void {
    if (this.completionRequested) return;
    this.pendingResult = result;
    this.completionRequested = true;
    this.resolveCompletion();
  }

  /** Vom Tracker gerufen, sobald der Kurs-Sync auf dem Server läuft. */
  allowBackground(): void {
    if (this.result) return;
    this.backgroundAvailable = true;
    this.cdr.markForCheck();
  }

  close(): void {
    this.dialogRef.close(this.result);
  }

  ngAfterViewInit(): void {
    if (this.initialResult) this.complete(this.initialResult);
    void this.run();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.animations.forEach((animation) => animation.cancel());
    this.animations.clear();
    this.resolveCompletion();
  }

  // -----------------------------------------------------------------------
  // Sequenz — `run()` aus sequences/split-swarm-distribute/sequence.js,
  // `animate` aus core/controller.js (`#runSequence`).
  // -----------------------------------------------------------------------

  private async run(): Promise<void> {
    const main = this.mainOrb?.sceneElements();
    const ring = this.ringRef?.nativeElement;
    const sparks = this.sparkRefs?.map((ref) => ref.nativeElement) ?? [];
    const bodies = this.miniBodyRefs?.map((ref) => ref.nativeElement) ?? [];
    const shadows = this.miniShadowRefs?.map((ref) => ref.nativeElement) ?? [];

    if (main && ring && bodies.length === MINI_ORBI_SIZES.length && shadows.length === MINI_ORBI_SIZES.length) {
      const frames = createSplitSwarmDistributeFrames();
      const options = { duration: SPLIT_SWARM_DISTRIBUTE_DURATION, easing: 'linear' };
      const splitAnimations = [
        this.animate(main.actor, frames.mainActor, options),
        this.animate(main.shadow, frames.mainShadow, options),
        this.animate(main.svg, frames.mainBlob, options),
        this.animate(ring, frames.burstRing, options),
        ...bodies.map((body, index) => this.animate(body, frames.minis[index].body, options)),
        ...shadows.map((shadow, index) => this.animate(shadow, frames.minis[index].shadow, options)),
        ...sparks.map((spark, index) => this.animate(spark, frames.sparks[index], options)),
      ];
      // Ergebnis-Modal: gleich den Endframe der Verteilung zeigen.
      if (this.initialResult) splitAnimations.forEach((animation) => animation.finish());
      await Promise.allSettled(splitAnimations.map(({ finished }) => finished));

      // Bei reduzierter Bewegung läuft jede Animation 1 ms (wie im Lab-
      // Controller) — der Warte-Loop würde dann im Millisekundentakt
      // kreiseln. Der Schwarm steht dort einfach verteilt, bis das
      // Ergebnis kommt.
      let cycleIndex = 0;
      while (!this.completionRequested && !this.destroyed && !this.reducedMotion) {
        const loopFrames = createMiniSwarmWaitingLoopFrames(cycleIndex);
        const loopOptions = {
          duration: MINI_DISTRIBUTION_LOOP_DURATION,
          easing: 'linear',
          transient: true,
        };
        const loopAnimations = [
          ...bodies.map((body, index) => this.animate(body, loopFrames[index].body, loopOptions)),
          ...shadows.map((shadow, index) => this.animate(shadow, loopFrames[index].shadow, loopOptions)),
        ];
        await Promise.allSettled(loopAnimations.map(({ finished }) => finished));
        cycleIndex += 1;
      }
    }

    await this.completion;
    if (this.destroyed) return;
    this.result = this.pendingResult;
    this.cdr.detectChanges();
  }

  /** Wie `animate` im Lab-Controller (plus `persist()`, siehe unten):
   *  `fill: 'both'` hält den Endzustand
   *  (der verteilte Schwarm bleibt stehen), `transient` nimmt eine
   *  Wartezyklus-Animation nach ihrem Ende wieder heraus — darunter liegt
   *  dann wieder der gehaltene Endframe der Verteilung, identisch zur
   *  Landeposition des Zyklus. */
  private animate(
    element: Element,
    keyframes: Keyframe[],
    options: { duration: number; easing: string; transient?: boolean },
  ): Animation {
    const { transient = false, ...animationOptions } = options;
    const animation = element.animate(keyframes, {
      fill: 'both',
      iterations: 1,
      ...animationOptions,
      duration: this.reducedMotion ? 1 : Math.max(1, Number(animationOptions.duration)),
    });
    animation.pause();
    animation.currentTime = 0;
    animation.play();
    animation.finished.catch(() => undefined);
    this.animations.add(animation);
    if (!transient) {
      // Ohne `persist()` entfernt der Browser die gehaltene Verteil-
      // Animation automatisch, sobald ein fertiger Wartezyklus dieselben
      // Eigenschaften vollständig überdeckt (Web-Animations „auto-remove").
      // Nimmt `cancel()` den Zyklus dann heraus, liegt nichts mehr darunter
      // — die Minis fielen auf `opacity: 0` zurück und waren nach dem
      // Abschluss verschwunden. Zwischen zwei Zyklen fiel das nur nicht
      // auf, weil der nächste im selben Frame startet.
      animation.persist();
    }
    if (transient) {
      animation.finished.then(
        () => {
          animation.cancel();
          this.animations.delete(animation);
        },
        () => this.animations.delete(animation),
      );
    }
    return animation;
  }
}
